import React, { useState, useEffect, useCallback } from 'react';
import Icon from './icons';

import { durationLabel } from './marketplaceDurations';
import TelegramSuppliersAdmin from './TelegramSuppliersAdmin';
import TelegramCategoryManager from './TelegramCategoryManager';
import TelegramNoticesAdmin from './TelegramNoticesAdmin';
import TelegramSupplierProducts from './TelegramSupplierProducts';
import TelegramMessageIcons from './TelegramMessageIcons';
import BulkPricesEditor from './BulkPricesEditor';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Admin for the Telegram shop.
 *
 * The bot sells the Shop's own catalogue, so products are not created here —
 * they come from Marketplace. What this page controls is the layer the bot adds
 * on top: what a Telegram buyer pays in USD, and which items are offered.
 *
 * Self-contained in the KeyRedemptionAdmin style — zero props, own authFetch,
 * own flash messages — so App.js only has to render <TelegramShopAdmin />.
 */
function TelegramShopAdmin() {
    const [sub, setSub] = useState('products');
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const [catalogue, setCatalogue] = useState([]);
    const [categories, setCategories] = useState([]);
    const [deposits, setDeposits] = useState([]);
    const [waitingDeposits, setWaitingDeposits] = useState(0);
    const [methods, setMethods] = useState([]);
    const [topups, setTopups] = useState([]);
    const [topupAgents, setTopupAgents] = useState([]);
    const [topupTotals, setTopupTotals] = useState({ credits: 0, cents: 0 });
    const [topupFilter, setTopupFilter] = useState('');
    // The month in view defaults to this one - the question is almost always
    // "what came in recently", and an empty range would load everything.
    const monthStart = new Date();
    const [walletFrom, setWalletFrom] = useState(
        `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}-01`);
    const [walletTo, setWalletTo] = useState(new Date().toISOString().slice(0, 10));
    const [walletTopups, setWalletTopups] = useState([]);
    const [walletAccounts, setWalletAccounts] = useState([]);
    const [walletTotalCents, setWalletTotalCents] = useState(0);
    const [amountEdits, setAmountEdits] = useState({});   // deposit id -> confirmed amount
    const [newMethod, setNewMethod] = useState({ key: '', label: '' });
    const [iconEdits, setIconEdits] = useState({});   // slug -> { emoji, customEmojiId, style }
    const [buttonStyles, setButtonStyles] = useState(['default', 'primary', 'success', 'danger']);
    const [defaultStyle, setDefaultStyle] = useState('primary');
    const [priced, setPriced] = useState(0);
    const [search, setSearch] = useState('');
    // Default to what can actually be sold. Leading with 200 sold-out rows
    // buries the handful the operator can do anything about.
    const [prodFilter, setProdFilter] = useState('sellable');
    const [shown, setShown] = useState(40);
    const [bulkPrice, setBulkPrice] = useState('');
    // The promotion being composed: { productId, durationMonths, name, oldUsd }
    const [promo, setPromo] = useState(null);
    const [bulkEdit, setBulkEdit] = useState(null);   // bulk-prices editor for one product+duration
    const [promoPrice, setPromoPrice] = useState('');
    const [promoHeadline, setPromoHeadline] = useState('');
    const [promoNote, setPromoNote] = useState('');
    const [edits, setEdits] = useState({});          // "productId:months" -> { usdPrice, visible }
    const [orders, setOrders] = useState([]);
    const [counts, setCounts] = useState({});
    const [revenue, setRevenue] = useState({ orders: 0, revenueUsd: 0 });
    const [buyers, setBuyers] = useState([]);
    const [supplierCount, setSupplierCount] = useState(0);
    const [supplierListKey, setSupplierListKey] = useState(0);   // bump to reload the supplier list after a promotion   // for the Products tab's "From suppliers" chip
    // Buyers are paged, searched and totalled on the server.
    const BUYER_PAGE = 50;
    const [buyerQ, setBuyerQ] = useState('');
    const [buyerQLive, setBuyerQLive] = useState('');   // what is typed; buyerQ follows it after a pause
    const [buyerFilter, setBuyerFilter] = useState('all');
    const [buyerSort, setBuyerSort] = useState('seen');
    const [buyerPage, setBuyerPage] = useState(0);
    const [buyerMeta, setBuyerMeta] = useState({ matching: 0, totals: { buyers: 0, heldCents: 0, withBalance: 0, blocked: 0, withOrders: 0 } });
    const [settings, setSettings] = useState({ welcomeText: '', adminChatIds: '', currency: 'USDT', webhookUrl: '', orderExpiryMinutes: '60', supportContact: '', channelLink: '' });
    const [status, setStatus] = useState({ botConfigured: false, binanceConfigured: false });
    // 'loading' | 'ok' | 'failed'. Until the server has actually answered, the
    // page must not claim the bot token or Binance keys are missing - an expired
    // login used to show exactly that false alarm.
    const [statusState, setStatusState] = useState('loading');

    const [fulfilDraft, setFulfilDraft] = useState({ orderId: null, payload: '' });
    const [orderFilter, setOrderFilter] = useState('');
    const [orderSearch, setOrderSearch] = useState('');   // what is typed
    const [orderQuery, setOrderQuery] = useState('');     // what is searched (debounced)
    useEffect(() => {
        const t = setTimeout(() => setOrderQuery(orderSearch.trim()), 350);
        return () => clearTimeout(t);
    }, [orderSearch]);

    const authFetch = useCallback(async (path, options = {}) => {
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        return { ok: res.ok, body: await res.json().catch(() => ({})) };
    }, []);

    const flash = (msg, isError = false) => {
        if (isError) { setError(msg); setNotice(null); } else { setNotice(msg); setError(null); }
        setTimeout(() => { setError(null); setNotice(null); }, 5000);
    };

    const loadDeposits = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/telegram/deposits');
        if (ok) { setDeposits(body.deposits || []); setWaitingDeposits(body.waiting || 0); }
    }, [authFetch]);

    const loadWalletTopups = useCallback(async (from, to) => {
        const query = new URLSearchParams();
        if (from) query.set('from', from);
        if (to) query.set('to', to);
        const { ok, body } = await authFetch(`/admin/telegram/wallet-topups?${query.toString()}`);
        if (ok) {
            setWalletTopups(body.topups || []);
            setWalletAccounts(body.accounts || []);
            setWalletTotalCents(body.totalCents || 0);
        }
    }, [authFetch]);

    const loadTopups = useCallback(async (reseller = '') => {
        const query = reseller ? `?reseller=${encodeURIComponent(reseller)}` : '';
        const { ok, body } = await authFetch(`/admin/telegram/topups${query}`);
        if (ok) {
            setTopups(body.topups || []);
            setTopupAgents(body.agents || []);
            setTopupTotals(body.totals || { credits: 0, cents: 0 });
        }
    }, [authFetch]);

    const loadMethods = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/telegram/payment-methods');
        if (ok) setMethods(body.methods || []);
    }, [authFetch]);

    const approveDeposit = async (d) => {
        // Default to what the buyer claimed, but the figure sent is whatever the
        // reviewer typed after looking at the payment itself.
        const raw = amountEdits[d.id] ?? (d.claimedCents ? (d.claimedCents / 100).toFixed(2) : '');
        const amountUsd = Number(raw);
        if (!(amountUsd > 0)) return flash('Enter the amount you actually received.', true);
        if (!window.confirm(`Credit $${amountUsd.toFixed(2)} to this buyer? This cannot be undone from here.`)) return;
        setBusy(true);
        const { ok, body } = await authFetch(`/admin/telegram/deposits/${d.id}/approve`, {
            method: 'POST', body: JSON.stringify({ amountUsd })
        });
        setBusy(false);
        if (!ok) return flash(body.error || 'Could not approve.', true);
        flash(`Credited $${amountUsd.toFixed(2)} — the buyer has been told.`);
        loadDeposits();
    };

    const rejectDeposit = async (d) => {
        const note = window.prompt('Why? The buyer sees this.', 'We could not find this payment.');
        if (note === null) return;
        setBusy(true);
        const { ok, body } = await authFetch(`/admin/telegram/deposits/${d.id}/reject`, {
            method: 'POST', body: JSON.stringify({ note })
        });
        setBusy(false);
        if (!ok) return flash(body.error || 'Could not reject.', true);
        flash('Rejected — the buyer has been told.');
        loadDeposits();
    };

    const saveMethod = async (key, fields) => {
        setBusy(true);
        const { ok, body } = await authFetch(`/admin/telegram/payment-methods/${key}`, {
            method: 'PUT', body: JSON.stringify(fields)
        });
        setBusy(false);
        if (!ok) return flash(body.error || 'Could not save.', true);
        flash('Saved.');
        loadMethods();
    };

    const uploadMethodQr = async (key, file) => {
        if (!file) return;
        setBusy(true);
        const form = new FormData();
        form.append('image', file);
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${API_URL}/admin/telegram/payment-methods/${key}/qr`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form
        });
        const body = await res.json().catch(() => ({}));
        setBusy(false);
        if (!res.ok) return flash(body.error || 'Upload failed.', true);
        flash('QR uploaded — buyers see it when they choose this method.');
        loadMethods();
    };

    const addMethod = async () => {
        const key = newMethod.key.trim().toLowerCase();
        if (!/^[a-z0-9-]{2,24}$/.test(key)) return flash('Use a short id like "alipay" or "wechat".', true);
        if (!newMethod.label.trim()) return flash('Give the method a name buyers will recognise.', true);
        await saveMethod(key, { label: newMethod.label.trim() });
        setNewMethod({ key: '', label: '' });
    };

    const loadCategories = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/telegram/categories');
        if (ok) {
            setCategories(body.categories || []);
            setIconEdits({});
            if (body.styles?.length) setButtonStyles(body.styles);
            if (body.defaultStyle) setDefaultStyle(body.defaultStyle);
        }
    }, [authFetch]);

    const uploadCategoryImage = async (slug, file) => {
        if (!file) return;
        setBusy(true);
        const form = new FormData();
        form.append('image', file);
        // Not authFetch: it forces application/json, and the browser must set
        // the multipart boundary itself.
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${API_URL}/admin/telegram/categories/${slug}/image`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form
        });
        const body = await res.json().catch(() => ({}));
        setBusy(false);
        if (!res.ok) return flash(body.error || 'Upload failed.', true);
        flash('Image uploaded — buyers see it at the top of that category.');
        loadCategories();
    };

    const uploadCategoryIcon = async (slug, file) => {
        if (!file) return;
        setBusy(true);
        const form = new FormData();
        form.append('image', file);
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${API_URL}/admin/telegram/categories/${slug}/icon`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form
        });
        const body = await res.json().catch(() => ({}));
        setBusy(false);
        if (!res.ok) return flash(body.error || 'Could not make an icon from that image.', true);
        flash('Icon created and live on the button.');
        loadCategories();
    };

    const removeCategoryImage = async (slug) => {
        setBusy(true);
        const { ok, body } = await authFetch(`/admin/telegram/categories/${slug}/image`, { method: 'DELETE' });
        setBusy(false);
        if (!ok) return flash(body.error || 'Could not remove the image.', true);
        flash('Image removed.');
        loadCategories();
    };

    const saveCategories = async () => {
        // Send every category, not just the edited ones: an emptied field means
        // "go back to the built-in icon", and that only reads as a change if the
        // row is present.
        const icons = categories.map(c => ({
            slug: c.slug,
            emoji: iconEdits[c.slug]?.emoji ?? c.emoji,
            customEmojiId: iconEdits[c.slug]?.customEmojiId ?? c.customEmojiId,
            style: iconEdits[c.slug]?.style ?? c.style
        }));
        setBusy(true);
        const { ok, body } = await authFetch('/admin/telegram/categories', {
            method: 'PUT', body: JSON.stringify({ icons })
        });
        if (ok) {
            // The shop-wide colour is a setting, not a category, so it saves
            // alongside rather than inside the icon map.
            await authFetch('/admin/telegram/settings', {
                method: 'PUT', body: JSON.stringify({
                    categoryButtonStyle: defaultStyle,
                    iconPackOwnerId: settings.iconPackOwnerId || ''
                })
            });
        }
        setBusy(false);
        if (!ok) return flash(body.error || 'Could not save.', true);
        flash('Icons saved — live in the bot now.');
        loadCategories();
    };

    const loadCatalogue = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/telegram/catalogue');
        if (ok) {
            setCatalogue(body.catalogue || []);
            setPriced(body.priced || 0);
            setEdits({});
        }
    }, [authFetch]);

    const loadOrders = useCallback(async () => {
        const params = new URLSearchParams();
        if (orderFilter) params.set('status', orderFilter);
        if (orderQuery) params.set('q', orderQuery);
        const qs = params.toString();
        const { ok, body } = await authFetch(`/admin/telegram/orders${qs ? '?' + qs : ''}`);
        if (ok) { setOrders(body.orders || []); setCounts(body.counts || {}); setRevenue(body.revenue || { orders: 0, revenueUsd: 0 }); }
    }, [authFetch, orderFilter, orderQuery]);

    const loadBuyers = useCallback(async () => {
        const qs = new URLSearchParams({
            q: buyerQ, filter: buyerFilter, sort: buyerSort,
            limit: String(BUYER_PAGE), offset: String(buyerPage * BUYER_PAGE)
        });
        const { ok, body } = await authFetch(`/admin/telegram/buyers?${qs}`);
        if (ok) {
            setBuyers(body.buyers || []);
            setBuyerMeta({ matching: body.matching || 0, totals: body.totals || {} });
        }
    }, [authFetch, buyerQ, buyerFilter, buyerSort, buyerPage]);

    // Search as you type, without a request per keystroke.
    useEffect(() => {
        const t = setTimeout(() => { setBuyerQ(buyerQLive.trim()); setBuyerPage(0); }, 350);
        return () => clearTimeout(t);
    }, [buyerQLive]);

    const loadSettings = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/telegram/settings');
        if (ok) { setSettings(s => ({ ...s, ...(body.settings || {}) })); setStatus(body.status || {}); setStatusState('ok'); }
        else setStatusState('failed');
    }, [authFetch]);

    useEffect(() => { loadCatalogue(); loadSettings(); loadCategories(); loadDeposits(); loadMethods(); loadTopups(); loadWalletTopups(walletFrom, walletTo); },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [loadCatalogue, loadSettings, loadCategories, loadDeposits, loadMethods, loadTopups, loadWalletTopups]);
    useEffect(() => { if (sub === 'orders') loadOrders(); }, [sub, loadOrders]);
    const loadSupplierCount = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/telegram/suppliers');
        if (ok) setSupplierCount((body.suppliers || []).reduce((n, s) => n + (s.products || 0), 0));
    }, [authFetch]);
    useEffect(() => { if (sub === 'products') loadSupplierCount(); }, [sub, loadSupplierCount]);
    useEffect(() => { if (sub === 'buyers') loadBuyers(); }, [sub, loadBuyers]);

    // Orders move on their own once a buyer pays, so keep this view live rather
    // than making the operator press refresh to notice a sale.
    useEffect(() => {
        if (sub !== 'orders') return undefined;
        const t = setInterval(loadOrders, 20000);
        return () => clearInterval(t);
    }, [sub, loadOrders]);

    const editKey = (productId, months) => `${productId}:${months}`;

    const editValue = (productId, months, field, fallback) => {
        const e = edits[editKey(productId, months)];
        return e && e[field] !== undefined ? e[field] : fallback;
    };

    const setEdit = (productId, months, field, value) => {
        const k = editKey(productId, months);
        setEdits(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [field]: value } }));
    };

    const sendPromotion = async () => {
        if (!promo) return;
        const newPrice = promoPrice.trim();
        if (newPrice && !(Number(newPrice) > 0)) return flash('The new price must be a number above zero.', true);
        if (newPrice && Number(newPrice) >= promo.oldUsd) {
            return flash(`The new price has to be below the current $${promo.oldUsd.toFixed(2)}.`, true);
        }
        const who = newPrice
            ? `Drop the price to $${Number(newPrice).toFixed(2)} and tell every buyer?`
            : 'Send this announcement to every buyer?';
        if (!window.confirm(who)) return;

        setBusy(true);
        let ok = false, body = {};
        try {
            ({ ok, body } = await authFetch('/admin/telegram/promote', {
                method: 'POST',
                body: JSON.stringify({
                    productId: promo.productId,
                    durationMonths: promo.durationMonths,
                    newUsdPrice: newPrice || undefined,
                    headline: promoHeadline.trim() || undefined,
                    note: promoNote.trim() || undefined
                })
            }));
        } catch (e) {
            body = { error: 'Could not reach the server — check your connection and try again.' };
        } finally {
            // Never leave every button disabled, whatever happened.
            setBusy(false);
        }
        if (!ok) return flash(body.error || 'Could not send.', true);
        flash(body.reason
            ? `Not sent: ${body.reason}`
            : body.queued
                ? `Sending to ${body.total} buyers in the background (about ${Math.max(1, Math.round(body.total / 20 / 60))} min)`
                  + `${body.ahead ? ` — after ${body.ahead} earlier promotion(s)` : ''}. You can promote another product now.`
                : `Sent to ${body.sent} buyer(s)${body.failed ? `, ${body.failed} unreachable` : ''}.`);
        setPromo(null); setPromoPrice(''); setPromoHeadline(''); setPromoNote('');
        loadCatalogue();
        setSupplierListKey(k => k + 1);
    };

    const saveCatalogue = async () => {
        const entries = Object.entries(edits).map(([k, v]) => {
            const [productId, durationMonths] = k.split(':').map(Number);
            return { productId, durationMonths, ...v };
        });
        if (!entries.length) return flash('Nothing has been changed.', true);
        setBusy(true);
        const { ok, body } = await authFetch('/admin/telegram/catalogue', {
            method: 'PUT', body: JSON.stringify({ entries })
        });
        setBusy(false);
        if (!ok) return flash(body.error || 'Could not save.', true);
        flash(`Saved ${body.saved} price(s) — live in the bot now.`);
        loadCatalogue();
    };

    // Typing one price and applying it to every duration of a product is the
    // common case; doing it per row for 200+ products would be unreasonable.
    const applyToAll = (p, value) => {
        for (const d of p.durations) setEdit(p.id, d.months, 'usdPrice', value);
    };

    /**
     * Price a whole set of products in one go and save immediately.
     *
     * Only durations that actually have stock are touched: pricing a sold-out
     * line changes nothing a buyer can see, and would quietly go live later when
     * stock returned at a price nobody reviewed.
     */
    const bulkApply = async (products, price) => {
        const value = Number(price);
        if (!(value > 0)) return flash('Enter a price greater than zero.', true);
        const entries = [];
        for (const p of products) {
            if (p.restricted) continue;
            for (const d of p.durations) {
                if (d.stock > 0) entries.push({ productId: p.id, durationMonths: d.months, usdPrice: value, visible: true });
            }
        }
        if (!entries.length) return flash('Nothing in this list has stock to price.', true);
        if (!window.confirm(`Set ${entries.length} option(s) across ${products.length} product(s) to $${value.toFixed(2)}? You can change any of them afterwards.`)) return;
        setBusy(true);
        const { ok, body } = await authFetch('/admin/telegram/catalogue', {
            method: 'PUT', body: JSON.stringify({ entries })
        });
        setBusy(false);
        if (!ok) return flash(body.error || 'Could not save.', true);
        flash(`${body.saved} option(s) priced — they are live in the bot now.`);
        setBulkPrice('');
        loadCatalogue();
    };


    const fulfil = async () => {
        if (!fulfilDraft.payload.trim()) return flash('Enter what should be sent to the buyer.', true);
        setBusy(true);
        const { ok, body } = await authFetch(`/admin/telegram/orders/${fulfilDraft.orderId}/fulfil`, {
            method: 'POST', body: JSON.stringify({ payload: fulfilDraft.payload })
        });
        setBusy(false);
        if (!ok) return flash(body.error || 'Could not fulfil the order.', true);
        flash(body.warning || 'Delivered — the buyer has been messaged.', !!body.warning);
        setFulfilDraft({ orderId: null, payload: '' });
        loadOrders();
    };

    const editNote = async (b) => {
        const note = window.prompt(
            `Note for ${b.username ? '@' + b.username : b.telegramUserId} — their group, their real name, anything.

` +
            'Only you see this; the buyer never does.', b.note || '');
        if (note === null) return;
        setBusy(true);
        const { ok, body } = await authFetch(`/admin/telegram/buyers/${b.telegramUserId}/note`, {
            method: 'POST', body: JSON.stringify({ note })
        });
        setBusy(false);
        if (!ok) return flash(body.error || 'Could not save the note.', true);
        flash(note.trim() ? 'Note saved.' : 'Note cleared.');
        loadBuyers();
    };

    const adjustBalance = async (b) => {
        const raw = window.prompt(
            `Adjust the bot balance for ${b.username ? '@' + b.username : b.telegramUserId}.

` +
            'Enter an amount in dollars — positive to add, negative to take away.', '10');
        if (raw === null) return;
        const amountUsd = Number(raw);
        if (!Number.isFinite(amountUsd) || amountUsd === 0) return flash('Enter a non-zero amount.', true);
        const reason = window.prompt('Why? The buyer sees this, and it goes in the ledger.',
            amountUsd > 0 ? 'Payment received outside the bot' : 'Correction');
        if (reason === null || !reason.trim()) return flash('A reason is required.', true);

        setBusy(true);
        const { ok, body } = await authFetch(`/admin/telegram/buyers/${b.telegramUserId}/balance`, {
            method: 'POST', body: JSON.stringify({ amountUsd, reason })
        });
        setBusy(false);
        if (!ok) return flash(body.error || 'Could not adjust.', true);
        flash(`Balance is now ${money(body.balanceCents / 100)} — the buyer has been told.`);
        loadBuyers();
        loadWalletTopups(walletFrom, walletTo);
    };

    const refund = async (o) => {
        const reason = window.prompt(
            `Refund ${money(o.priceUsd)} to ${o.telegramUsername ? '@' + o.telegramUsername : o.telegramUserId}?

` +
            'The buyer sees this reason. Their balance is credited immediately.',
            'Sorry, we could not fulfil this order.');
        if (reason === null) return;
        setBusy(true);
        const { ok, body } = await authFetch(`/admin/telegram/orders/${o.id}/refund`, {
            method: 'POST', body: JSON.stringify({ reason })
        });
        setBusy(false);
        if (!ok) return flash(body.error || 'Could not refund.', true);
        flash(`Refunded ${money(body.refundedCents / 100)} — the buyer has been told.`);
        loadOrders();
    };

    const recheck = async (id) => {
        const { ok, body } = await authFetch(`/admin/telegram/orders/${id}/recheck`, { method: 'POST' });
        if (!ok) return flash(body.error || 'Could not check that payment.', true);
        const r = body.result || {};
        flash(r.delivered ? 'Payment confirmed and delivered.'
            : r.pendingManual ? 'Payment confirmed — the order is waiting for you to fulfil it.'
            : r.already ? 'Already settled.'
            : `Not paid yet${r.binanceStatus ? ` (Binance says: ${r.binanceStatus})` : ''}.`);
        loadOrders();
    };

    const saveSettings = async () => {
        const { ok, body } = await authFetch('/admin/telegram/settings', { method: 'PUT', body: JSON.stringify(settings) });
        if (!ok) return flash(body.error || 'Could not save settings.', true);
        flash('Settings saved.');
        loadSettings();
    };

    const money = n => '$' + Number(n || 0).toFixed(2);
    const statusLabel = {
        pending_payment: 'Awaiting payment', paid: 'Paid', awaiting_fulfilment: 'Needs fulfilment',
        delivered: 'Delivered', expired: 'Expired', cancelled: 'Cancelled', refunded: 'Refunded'
    };
    const statusColor = {
        pending_payment: '#b45309', paid: '#0369a1', awaiting_fulfilment: '#b91c1c',
        delivered: '#15803d', expired: '#6b7280', cancelled: '#6b7280', refunded: '#7c3aed'
    };

    const tabs = [
        { key: 'products', label: 'Products', icon: 'package' },
        { key: 'orders', label: `Orders${counts.awaiting_fulfilment ? ` (${counts.awaiting_fulfilment})` : ''}`, icon: 'cart' },
        { key: 'buyers', label: 'Buyers', icon: 'users' },
        { key: 'deposits', label: `Deposits${waitingDeposits ? ` (${waitingDeposits})` : ''}`, icon: 'card' },
        { key: 'topups', label: 'Credit top-ups', icon: 'chart' },
        { key: 'categories', label: 'Categories', icon: 'image' },
        { key: 'suppliers', label: 'Suppliers', icon: 'link' },
        { key: 'notices', label: 'Notices', icon: 'bell' },
        { key: 'settings', label: 'Settings', icon: 'settings' },
    ];

    return (
        <div className="tg-admin">
            <div className="tg-head">
                <div>
                    <p className="tg-sub" style={{ marginTop: 0 }}>
                        The bot sells your <b>Marketplace</b> catalogue from the same stock. Set what a Telegram
                        buyer pays in US dollars; an item goes live as soon as it has a price and stock.
                    </p>
                </div>
                {statusState === 'ok' && <div className="tg-status">
                    <span className={`tg-pill ${status.botConfigured ? 'ok' : 'bad'}`}>
                        {status.botConfigured ? 'Bot connected' : 'Bot token missing'}
                    </span>
                    <span className={`tg-pill ${status.binanceWorking ? 'ok' : 'bad'}`}
                        title={status.binanceDetail || ''}>
                        {!status.binanceConfigured ? 'Binance not configured'
                            : status.binanceWorking ? 'Payments ready'
                            : 'Binance key rejected'}
                    </span>
                </div>}
            </div>

            {statusState === 'failed' && (
                <div className="tg-note bad">
                    <b>Could not load the bot's status.</b> Your login has probably expired — log out and log in again.
                    Nothing is wrong with the bot itself.
                </div>
            )}

            {statusState === 'ok' && !status.binanceConfigured && (
                <div className="tg-note bad">
                    <b>Checkout is disabled.</b> Set <code>BINANCE_API_KEY</code>, <code>BINANCE_API_SECRET</code> and{' '}
                    <code>BINANCE_PAY_ID</code> in <code>server/.env</code>. The key must be a read-only Binance
                    account key — not a Binance Pay merchant key.
                </div>
            )}
            {status.binanceConfigured && !status.binanceWorking && status.binanceDetail && (
                <div className="tg-note bad">
                    <b>Checkout is disabled.</b> {status.binanceDetail}
                </div>
            )}
            {status.binanceWorking && status.payId && (
                <div className="tg-note ok">
                    Buyers are told to pay <b>{status.payId}</b>, then paste their Binance Order ID in the chat.
                    Each payment is verified against your own transaction history and can only be used once.
                </div>
            )}

            {notice && <div className="tg-note ok">{notice}</div>}
            {error && <div className="tg-note bad">{error}</div>}

            <div className="tg-tabs">
                {tabs.map(t => (
                    <button key={t.key} className={`tg-tab ${sub === t.key ? 'active' : ''}`} onClick={() => setSub(t.key)}>
                        <Icon name={t.icon} size={15} /> {t.label}
                    </button>
                ))}
            </div>

            {sub === 'products' && (() => {
                // Derive once, so the chips, the counts and the list can never
                // disagree about what "live" means.
                const flag = p => {
                    if (p.restricted) return 'restricted';
                    const anyStock = p.durations.some(d => d.stock > 0);
                    const anyPriced = p.durations.some(d => d.usdPrice > 0);
                    const anyLive = p.durations.some(d => d.usdPrice > 0 && d.visible && d.stock > 0);
                    if (anyLive) return 'live';
                    if (!anyStock) return 'soldout';
                    return anyPriced ? 'hidden' : 'unpriced';
                };
                const tagged = catalogue.map(p => ({ ...p, flag: flag(p) }));
                const tally = tagged.reduce((a, p) => ({ ...a, [p.flag]: (a[p.flag] || 0) + 1 }), {});

                const filters = [
                    { key: 'sellable', label: 'Needs a price', match: p => p.flag === 'unpriced', count: tally.unpriced || 0 },
                    { key: 'live', label: 'Live in bot', match: p => p.flag === 'live', count: tally.live || 0 },
                    { key: 'hidden', label: 'Hidden', match: p => p.flag === 'hidden', count: tally.hidden || 0 },
                    { key: 'soldout', label: 'Sold out', match: p => p.flag === 'soldout', count: tally.soldout || 0 },
                    { key: 'restricted', label: 'Reseller only', match: p => p.flag === 'restricted', count: tally.restricted || 0 },
                    { key: 'all', label: 'All', match: () => true, count: tagged.length },
                ];
                const active = filters.find(f => f.key === prodFilter) || filters[0];
                const q = search.trim().toLowerCase();
                const list = tagged.filter(p => active.match(p) && (!q || p.name.toLowerCase().includes(q)));
                const dirty = Object.keys(edits).length;

                const sellableNow = list.filter(p => !p.restricted && p.durations.some(d => d.stock > 0));

                const toolbarEl = (
                        <div className="tg-toolbar">
                            <input className="tg-search" value={search} onChange={e => { setSearch(e.target.value); setShown(40); }}
                                placeholder={prodFilter === 'suppliers' ? `Search ${supplierCount} supplier products…` : `Search ${catalogue.length} products…`} />
                            <div className="tg-filters">
                                {filters.map(f => (
                                    <button key={f.key} className={`tg-chip ${prodFilter === f.key ? 'active' : ''}`}
                                        onClick={() => { setProdFilter(f.key); setShown(40); }}>
                                        {f.label} <span className="tg-chip-n">{f.count}</span>
                                    </button>
                                ))}
                                <button className={`tg-chip ${prodFilter === 'suppliers' ? 'active' : ''}`}
                                    title="Products from your linked suppliers (ProdSeller, HyleHub, InsightXPro…)"
                                    onClick={() => { setProdFilter('suppliers'); setShown(40); }}>
                                    🔗 From suppliers <span className="tg-chip-n">{supplierCount}</span>
                                </button>
                            </div>
                        </div>
                );

                // Supplier products live in their own list, searched by the same box.
                if (prodFilter === 'suppliers') {
                    return (
                        <>
                            {toolbarEl}
                            <TelegramSupplierProducts key={supplierListKey} search={search} onChanged={loadSupplierCount}
                                onPromote={r => setPromo({ productId: r.botId, durationMonths: r.months, name: r.name, supplierName: r.supplierName, oldUsd: Number(r.sellUsd), costUsd: Number(r.costUsd) })} />
                        </>
                    );
                }

                return (
                    <>
                        {priced === 0 && (
                            <div className="tg-quickstart">
                                <div>
                                    <b>Your bot has nothing to show yet.</b>
                                    <p>
                                        Products only appear once they have a price. Set one price across everything
                                        now to get the shop live, then adjust individual items afterwards.
                                    </p>
                                </div>
                                <div className="tg-quickstart-act">
                                    <span>$</span>
                                    <input type="number" step="0.01" min="0" value={bulkPrice}
                                        onChange={e => setBulkPrice(e.target.value)} placeholder="9.99" />
                                    <button className="tg-btn primary" disabled={busy}
                                        onClick={() => bulkApply(catalogue, bulkPrice)}>
                                        Price everything
                                    </button>
                                </div>
                            </div>
                        )}

                        {toolbarEl}

                        {priced > 0 && sellableNow.length > 0 && (
                            <div className="tg-bulkbar">
                                <span>Apply one price to all <b>{sellableNow.length}</b> shown</span>
                                <span className="tg-bulk-in">$
                                    <input type="number" step="0.01" min="0" value={bulkPrice}
                                        onChange={e => setBulkPrice(e.target.value)} placeholder="9.99" />
                                </span>
                                <button className="tg-btn sm" disabled={busy}
                                    onClick={() => bulkApply(sellableNow, bulkPrice)}>Apply</button>
                            </div>
                        )}

                        <div className="tg-summary">
                            <span><b>{priced}</b> live in the bot</span>
                            <span><b>{tally.unpriced || 0}</b> waiting for a price</span>
                            <span><b>{catalogue.length}</b> in Marketplace</span>
                        </div>

                        {!list.length && (
                            <div className="tg-card">
                                <p className="tg-empty">
                                    {q ? `Nothing matches “${search}”.` : `Nothing in “${active.label}”.`}
                                </p>
                            </div>
                        )}

                        {list.slice(0, shown).map(p => (
                            <div key={p.id} className={`tg-prod ${p.flag}`}>
                                <div className="tg-prod-head">
                                    <div className="tg-prod-name">
                                        {p.pinned && <span title="Pinned in Marketplace">⭐</span>}
                                        <span>{p.name}</span>
                                    </div>
                                    <div className="tg-prod-tags">
                                        {p.manualFulfill && <span className="tg-tag">manual</span>}
                                        {p.requiresEmail && <span className="tg-tag">email</span>}
                                        <span className={`tg-state ${p.flag}`}>
                                            {{ live: 'Live', hidden: 'Hidden', unpriced: 'No price',
                                               soldout: 'Sold out', restricted: 'Reseller only' }[p.flag]}
                                        </span>
                                    </div>
                                </div>

                                {p.restricted ? (
                                    <p className="tg-prod-note">
                                        Reserved for named resellers in Marketplace, so it is never offered to public buyers.
                                    </p>
                                ) : (
                                    <div className="tg-dur-grid">
                                        {p.durations.map(d => {
                                            // The box shows only what was TYPED here. A followed price
                                            // sits in the placeholder, so leaving the box empty keeps it
                                            // following the Shop instead of freezing today's number.
                                            const price = editValue(p.id, d.months, 'usdPrice',
                                                d.typedPrice != null ? String(d.typedPrice) : '');
                                            const visible = editValue(p.id, d.months, 'visible', d.visible);
                                            const followed = d.priceSource === 'shop' ? d.usdPrice : null;
                                            const effective = Number(price) > 0 ? Number(price) : followed;
                                            const live = effective > 0 && visible && d.stock > 0;
                                            return (
                                                <div key={d.months} className={`tg-dur ${d.stock > 0 ? '' : 'nostock'} ${live ? 'live' : ''}`}>
                                                    <div className="tg-dur-top">
                                                        <b>{durationLabel(d.months)}</b>
                                                        <span>{d.stock > 0 ? `${d.stock} in stock` : 'sold out'}</span>
                                                    </div>
                                                    <div className="tg-dur-input">
                                                        <span>$</span>
                                                        <input type="number" step="0.01" min="0" value={price}
                                                            disabled={d.stock === 0}
                                                            placeholder={d.stock === 0 ? '—' : followed > 0 ? followed.toFixed(2) : 'price'}
                                                            title={followed > 0 ? `Following the Shop: $${followed.toFixed(2)}. Type a price to override it.` : ''}
                                                            onChange={e => setEdit(p.id, d.months, 'usdPrice', e.target.value)} />
                                                        <label title="Offer this in the bot">
                                                            <input type="checkbox" checked={!!visible} disabled={d.stock === 0}
                                                                onChange={e => setEdit(p.id, d.months, 'visible', e.target.checked)} />
                                                        </label>
                                                    </div>
                                                    {followed > 0 && !(Number(price) > 0) && (
                                                        <div className="tg-dur-follow">follows Shop · ${followed.toFixed(2)}</div>
                                                    )}
                                                    {effective > 0 && !p.manualFulfill && !p.requiresEmail && (
                                                        <button type="button" className="tg-promote"
                                                            title="Lower unit price when a buyer takes several at once"
                                                            onClick={() => setBulkEdit({ productId: p.id, durationMonths: d.months,
                                                                title: `${p.name} — ${durationLabel(d.months)}`, baseUsd: Number(effective), tiers: d.bulkTiers || [] })}>
                                                            🏷 Bulk prices{(d.bulkTiers || []).length ? ` (${d.bulkTiers.length})` : ''}
                                                        </button>
                                                    )}
                                                    {effective > 0 && d.stock > 0 && visible && (
                                                        <button type="button" className="tg-promote"
                                                            title="Announce this price to every buyer"
                                                            onClick={() => {
                                                                setPromo({ productId: p.id, durationMonths: d.months, name: p.name, oldUsd: Number(effective) });
                                                                setPromoPrice(''); setPromoHeadline(''); setPromoNote('');
                                                            }}>📣 Promote</button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {p.durations.filter(d => d.stock > 0).length > 1 && (
                                            <div className="tg-dur tg-dur-all">
                                                <div className="tg-dur-top"><b>All durations</b><span>set at once</span></div>
                                                <div className="tg-dur-input">
                                                    <span>$</span>
                                                    <input type="number" step="0.01" min="0" placeholder="price"
                                                        onChange={e => applyToAll(p, e.target.value)} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}

                        {list.length > shown && (
                            <button className="tg-btn tg-more" onClick={() => setShown(n => n + 60)}>
                                Show more ({list.length - shown} remaining)
                            </button>
                        )}

                        {/* Stays on screen while you scroll, so edits made 200 rows
                            down are never lost to a save button at the top. */}
                        {dirty > 0 && (
                            <div className="tg-savebar">
                                <span>{dirty} unsaved change{dirty === 1 ? '' : 's'}</span>
                                <div>
                                    <button className="tg-btn sm" onClick={() => setEdits({})}>Discard</button>
                                    <button className="tg-btn primary sm" disabled={busy} onClick={saveCatalogue}>Save</button>
                                </div>
                            </div>
                        )}
                    </>
                );
            })()}

            {sub === 'orders' && (
                <div className="tg-card">
                    <div className="tg-orders-head">
                        <h3>Orders</h3>
                        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 360 }}>
                            <input className="tg-input" type="search" value={orderSearch} onChange={e => setOrderSearch(e.target.value)}
                                placeholder="Search order # (e.g. 280), buyer, product, code…"
                                style={{ width: '100%', margin: 0 }} />
                        </div>
                        <div className="tg-revenue">{revenue.orders} paid · <b>{money(revenue.revenueUsd)}</b></div>
                    </div>
                    <div className="tg-filters">
                        {['', 'awaiting_fulfilment', 'pending_payment', 'delivered', 'expired'].map(s => (
                            <button key={s || 'all'} className={`tg-chip ${orderFilter === s ? 'active' : ''}`} onClick={() => setOrderFilter(s)}>
                                {s ? statusLabel[s] : 'All'}{counts[s] ? ` (${counts[s]})` : ''}
                            </button>
                        ))}
                    </div>
                    {!orders.length && <p className="tg-empty">{orderQuery ? `No order matches “${orderQuery}”.` : 'No orders here yet.'}</p>}
                    {orders.map(o => {
                        // The buyer's activation email is kept in the order note ("activation email: x@y").
                        const note = String(o.adminNote || '');
                        const actEmail = (/activation email:s*(S+)/i.exec(note) || [])[1] || '';
                        const otherNote = note.replace(/activation email:s*S+/i, '').trim();
                        const copyEmail = () => {
                            const done = () => flash(`Copied ${actEmail}`);
                            if (navigator.clipboard) navigator.clipboard.writeText(actEmail).then(done, () => window.prompt('Copy the email:', actEmail));
                            else window.prompt('Copy the email:', actEmail);
                        };
                        const emailLine = actEmail && (
                            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 600 }}>📧 Activate on:</span>
                                <code style={{ fontSize: 14, padding: '2px 8px', borderRadius: 6, background: 'rgba(37,99,235,0.1)', userSelect: 'all' }}>{actEmail}</code>
                                <button className="tg-btn sm" onClick={copyEmail}>Copy</button>
                            </div>
                        );
                        return (
                        <div key={o.id} className="tg-row">
                            <div className="tg-row-main">
                                <div className="tg-row-title">
                                    #{o.id} · {o.productName}
                                    <span className="tg-tag" style={{ background: statusColor[o.status] || '#6b7280' }}>
                                        {statusLabel[o.status] || o.status}
                                    </span>
                                    {o.source && o.source.type === 'supplier'
                                        ? <span className="tg-tag" style={{ background: '#7c3aed' }}
                                            title={o.source.remoteOrderId ? `Order ${o.source.remoteOrderId} at ${o.source.supplier}` : `Bought from ${o.source.supplier}`}>
                                            🔗 {o.source.supplier}
                                          </span>
                                        : <span className="tg-tag" style={{ background: '#475569' }} title="Delivered from your own stock">🏠 From admin</span>}
                                </div>
                                <div className="tg-row-meta">
                                    {money(o.priceUsd)} · {o.telegramUsername ? '@' + o.telegramUsername : 'id ' + o.telegramUserId}
                                    {' · '}{new Date(o.createdAt).toLocaleString()}
                                    {o.deliveredPayload && <> · delivered <code>{o.deliveredPayload}</code></>}
                                </div>
                                {o.source && o.source.type === 'supplier' && (o.source.remoteOrderId || o.source.costUsd > 0) && (
                                    <div className="tg-row-meta" style={{ marginTop: 2 }}>
                                        {o.source.remoteOrderId && <>{o.source.supplier} order <code>{o.source.remoteOrderId}</code></>}
                                        {o.source.costUsd > 0 && <>
                                            {o.source.remoteOrderId ? ' · ' : ''}cost {money(o.source.costUsd)}
                                            {' · '}profit <b style={{ color: Number(o.priceUsd) - o.source.costUsd > 0 ? '#16a34a' : '#dc2626' }}>{money(Number(o.priceUsd) - o.source.costUsd)}</b>
                                        </>}
                                    </div>
                                )}
                                {emailLine}
                                {otherNote && <div className="tg-row-meta" style={{ marginTop: 4, fontStyle: 'italic' }}>Note: {otherNote}</div>}
                            </div>
                            <div className="tg-row-actions">
                                {o.status === 'awaiting_fulfilment' && (
                                    <button className="tg-btn sm primary" onClick={() => setFulfilDraft({ orderId: o.id, payload: '' })}>Fulfil</button>
                                )}
                                {o.status === 'pending_payment' && (
                                    <button className="tg-btn sm" onClick={() => recheck(o.id)}>Check payment</button>
                                )}
                                {['paid', 'awaiting_fulfilment', 'delivered'].includes(o.status) && (
                                    <button className="tg-btn sm" disabled={busy} onClick={() => refund(o)}
                                        title="Return the money to the buyer's balance">↩ Refund</button>
                                )}
                            </div>
                            {fulfilDraft.orderId === o.id && (
                                <div className="tg-inline">
                                    {actEmail && <div style={{ marginBottom: 6 }}>Buyer's activation email: <b>{actEmail}</b></div>}
                                    <textarea rows={3} value={fulfilDraft.payload} placeholder="What to send the buyer — account details, code, instructions…"
                                        onChange={e => setFulfilDraft({ ...fulfilDraft, payload: e.target.value })} />
                                    <div className="tg-actions">
                                        <button className="tg-btn primary sm" disabled={busy} onClick={fulfil}>Send & mark delivered</button>
                                        <button className="tg-btn sm" onClick={() => setFulfilDraft({ orderId: null, payload: '' })}>Cancel</button>
                                    </div>
                                </div>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}

            {sub === 'buyers' && (
                <div className="tg-card">
                    <h3>
                        Buyers ({(buyerMeta.totals.buyers || 0).toLocaleString()})
                        <span style={{ float: 'right', fontSize: 13, fontWeight: 400, opacity: 0.85 }}>
                            {money((buyerMeta.totals.heldCents || 0) / 100)} held on account by {buyerMeta.totals.withBalance || 0} buyer(s)
                        </span>
                    </h3>
                    <div className="tg-toolbar">
                        <input className="tg-search" value={buyerQLive} onChange={e => setBuyerQLive(e.target.value)}
                            placeholder="Search username, name, note or Telegram id…" />
                        <select className="tg-input" value={buyerSort} onChange={e => { setBuyerSort(e.target.value); setBuyerPage(0); }}>
                            <option value="seen">Last seen</option>
                            <option value="balance">Highest balance</option>
                            <option value="spent">Most spent</option>
                            <option value="paid">Most paid in</option>
                            <option value="joined">Newest</option>
                        </select>
                    </div>
                    <div className="tg-filters" style={{ margin: '8px 0 4px' }}>
                        {[
                            ['all', 'All', buyerMeta.totals.buyers],
                            ['balance', 'Has balance', buyerMeta.totals.withBalance],
                            ['orders', 'Has orders', buyerMeta.totals.withOrders],
                            ['blocked', 'Blocked', buyerMeta.totals.blocked],
                        ].map(([key, label, n]) => (
                            <button key={key} className={`tg-chip ${buyerFilter === key ? 'active' : ''}`}
                                onClick={() => { setBuyerFilter(key); setBuyerPage(0); }}>
                                {label} <span className="tg-chip-n">{(n || 0).toLocaleString()}</span>
                            </button>
                        ))}
                    </div>
                    {(() => {
                        const pages = Math.max(1, Math.ceil((buyerMeta.matching || 0) / BUYER_PAGE));
                        const first = buyerMeta.matching ? buyerPage * BUYER_PAGE + 1 : 0;
                        const last = Math.min((buyerPage + 1) * BUYER_PAGE, buyerMeta.matching || 0);
                        return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0 10px', fontSize: 13 }}>
                                <span>Showing <b>{first}–{last}</b> of <b>{(buyerMeta.matching || 0).toLocaleString()}</b></span>
                                <span style={{ flex: 1 }} />
                                <button className="tg-btn sm" disabled={buyerPage === 0} onClick={() => setBuyerPage(0)}>« First</button>
                                <button className="tg-btn sm" disabled={buyerPage === 0} onClick={() => setBuyerPage(p => p - 1)}>‹ Prev</button>
                                <span>Page {buyerPage + 1} / {pages}</span>
                                <button className="tg-btn sm" disabled={buyerPage + 1 >= pages} onClick={() => setBuyerPage(p => p + 1)}>Next ›</button>
                                <button className="tg-btn sm" disabled={buyerPage + 1 >= pages} onClick={() => setBuyerPage(pages - 1)}>Last »</button>
                            </div>
                        );
                    })()}
                    {!buyers.length && <p className="tg-empty">{buyerQ || buyerFilter !== 'all' ? 'No buyers match.' : 'Nobody has used the bot yet.'}</p>}
                    {buyers.map(b => (
                        <div key={b.telegramUserId} className="tg-row">
                            <div className="tg-row-main">
                                <div className="tg-row-title">
                                    {b.username ? '@' + b.username : b.firstName || 'Unknown'}
                                    {b.note && (
                                        <span style={{ fontWeight: 400, opacity: 0.85 }}> ({b.note})</span>
                                    )}
                                    {b.balanceCents > 0 && (
                                        <span className="tg-tag" style={{ background: '#15803d' }}>
                                            {money(b.balanceCents / 100)}
                                        </span>
                                    )}
                                    {b.blocked === 1 && <span className="tg-tag" style={{ background: '#b91c1c' }}>blocked</span>}
                                </div>
                                <div className="tg-row-meta">
                                    id {b.telegramUserId} · balance <b>{money((b.balanceCents || 0) / 100)}</b>
                                    {b.totalDepositedCents > 0 && <> · paid in {money(b.totalDepositedCents / 100)}</>}
                                    {b.totalSpentCents > 0 && <> · spent {money(b.totalSpentCents / 100)}</>}
                                    {' · '}{b.orders} order(s) · last seen {new Date(b.lastSeenAt).toLocaleString()}
                                </div>
                            </div>
                            <div className="tg-row-actions">
                                <button className="tg-btn sm" disabled={busy}
                                    title="Add to or take from this buyer's bot balance"
                                    onClick={() => adjustBalance(b)}>Balance</button>
                                <button className="tg-btn sm" disabled={busy}
                                    title="Your own note — the buyer never sees it"
                                    onClick={() => editNote(b)}>{b.note ? 'Edit note' : 'Note'}</button>
                                <button className="tg-btn sm" onClick={async () => {
                                    await authFetch(`/admin/telegram/buyers/${b.telegramUserId}/block`, {
                                        method: 'POST', body: JSON.stringify({ blocked: b.blocked !== 1 })
                                    });
                                    loadBuyers();
                                }}>{b.blocked === 1 ? 'Unblock' : 'Block'}</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {sub === 'topups' && (
                <div className="tg-card">
                    <h3 style={{ marginTop: 0 }}>Credit top-ups made in the bot</h3>
                    <p className="tg-sub">
                        Which agent topped up which reseller, what it cost them, and when. The reseller also sees
                        each one in their own transaction history, so the two records agree.
                    </p>

                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                        <input className="tg-input" style={{ width: 220 }} placeholder="Filter by reseller username"
                            value={topupFilter} onChange={e => setTopupFilter(e.target.value)} />
                        <button className="tg-btn sm" onClick={() => loadTopups(topupFilter.trim())}>Filter</button>
                        {topupFilter && (
                            <button className="tg-btn sm" onClick={() => { setTopupFilter(''); loadTopups(''); }}>Clear</button>
                        )}
                        <span style={{ fontSize: 13, opacity: 0.8 }}>
                            <b>{topupTotals.credits.toFixed(2)}</b> credits &middot;{' '}
                            <b>${(topupTotals.cents / 100).toFixed(2)}</b> across <b>{topups.length}</b> top-up(s)
                        </span>
                    </div>

                    {topupAgents.length > 1 && (
                        <div style={{ marginBottom: 16 }}>
                            <b style={{ fontSize: 13 }}>By agent</b>
                            <table className="tg-table" style={{ marginTop: 6 }}>
                                <thead>
                                    <tr>
                                        <th>Agent</th>
                                        <th style={{ width: 110 }}>Top-ups</th>
                                        <th style={{ width: 130 }}>Credits</th>
                                        <th style={{ width: 130 }}>Paid</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topupAgents.map(a => (
                                        <tr key={a.agent}>
                                            <td><b>{a.agent}</b></td>
                                            <td>{a.topups}</td>
                                            <td>{a.credits.toFixed(2)}</td>
                                            <td>${(a.cents / 100).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!topups.length ? (
                        <p className="tg-sub">No top-ups yet.</p>
                    ) : (
                        <table className="tg-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 60 }}>#</th>
                                    <th style={{ width: 160 }}>When</th>
                                    <th>Agent (who paid)</th>
                                    <th>Reseller topped up</th>
                                    <th style={{ width: 110 }}>Credits</th>
                                    <th style={{ width: 110 }}>Charged</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topups.map(t => (
                                    <tr key={t.id}>
                                        <td>{t.id}</td>
                                        <td>{String(t.createdAt || '').slice(0, 16).replace('T', ' ')}</td>
                                        <td>
                                            <b>{t.telegramUsername ? '@' + t.telegramUsername : t.telegramUserId}</b>
                                            {t.telegramUsername && (
                                                <div style={{ fontSize: 11, opacity: 0.6 }}>{t.telegramUserId}</div>
                                            )}
                                        </td>
                                        <td><b>{t.resellerUsername}</b></td>
                                        <td>{Number(t.credits).toFixed(2)}</td>
                                        <td>${(t.usdCents / 100).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {bulkEdit && (
                <BulkPricesEditor {...bulkEdit} onClose={() => setBulkEdit(null)}
                    onSaved={(tiers) => {
                        flash(tiers.length ? `Bulk prices saved (${tiers.length} tier${tiers.length === 1 ? '' : 's'}).` : 'Bulk prices removed.');
                        loadCatalogue();
                    }} />
            )}

            {promo && (
                <div className="tg-modal-back" onClick={() => setPromo(null)}>
                    <div className="tg-modal" onClick={e => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Promote to every buyer</h3>
                        <p className="tg-sub">
                            <b>{promo.name}</b>{promo.durationMonths !== 1000 && <> — {durationLabel(promo.durationMonths)}</>},
                            currently <b>${promo.oldUsd.toFixed(2)}</b>.
                            {promo.supplierName && <> From <b>{promo.supplierName}</b> — your cost <b>${promo.costUsd.toFixed(2)}</b>; a promo price must stay above it.</>}
                        </p>

                        <label className="tg-label">New price (optional)</label>
                        <input className="tg-input" type="number" step="0.01" min="0"
                            placeholder={promo.costUsd ? `between ${(promo.costUsd + 0.01).toFixed(2)} and ${(promo.oldUsd - 0.01).toFixed(2)}` : `below ${promo.oldUsd.toFixed(2)}`}
                            value={promoPrice} onChange={e => setPromoPrice(e.target.value)} />
                        <span style={{ display: 'block', fontSize: 12, opacity: 0.7, margin: '4px 0 12px' }}>
                            Leave empty to announce the current price without changing it. If you set one, it is applied
                            first, so the price on the button is the price buyers are charged.
                        </span>

                        <label className="tg-label">Headline</label>
                        <input className="tg-input" placeholder="Amazing Discount!"
                            value={promoHeadline} onChange={e => setPromoHeadline(e.target.value)} />

                        <label className="tg-label" style={{ marginTop: 12 }}>Extra line (optional)</label>
                        <input className="tg-input" placeholder="e.g. Today only"
                            value={promoNote} onChange={e => setPromoNote(e.target.value)} />

                        <div className="tg-note" style={{ marginTop: 14 }}>
                            <b>Preview</b>
                            <div style={{ marginTop: 6, lineHeight: 1.6 }}>
                                🎉 <b>{promoHeadline.trim() || 'Amazing Discount!'}</b> 🔥<br />
                                🛒 Product: <b>{promo.name}</b><br />
                                {promo.durationMonths !== 1000 && <>⏳ {durationLabel(promo.durationMonths)}<br /></>}
                                {Number(promoPrice) > 0 && Number(promoPrice) < promo.oldUsd ? (
                                    <>
                                        Old price: <s>${promo.oldUsd.toFixed(2)}</s><br />
                                        New price: <b>${Number(promoPrice).toFixed(2)}</b> only!<br />
                                        💸 Save <b>{Math.round((1 - Number(promoPrice) / promo.oldUsd) * 100)}%</b><br />
                                    </>
                                ) : (
                                    <>💵 Price: <b>${promo.oldUsd.toFixed(2)}</b><br /></>
                                )}
                                {promoNote.trim() && <>{promoNote.trim()}<br /></>}
                                Hurry and buy now from the store! 🛒
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                            <button className="tg-btn primary" disabled={busy} onClick={sendPromotion}>
                                Send to all buyers
                            </button>
                            <button className="tg-btn" disabled={busy} onClick={() => setPromo(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {sub === 'deposits' && (
                <div className="tg-card">
                    <h3 style={{ marginTop: 0 }}>Top-ups by account</h3>
                    <p className="tg-sub">
                        Money that came into each Telegram account&rsquo;s balance, however it was paid. What the
                        balance was later spent on is a separate question and is not mixed in here.
                    </p>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                        <span style={{ fontSize: 13 }}>From</span>
                        <input className="tg-input" style={{ width: 150 }} type="date"
                            value={walletFrom} onChange={e => setWalletFrom(e.target.value)} />
                        <span style={{ fontSize: 13 }}>to</span>
                        <input className="tg-input" style={{ width: 150 }} type="date"
                            value={walletTo} onChange={e => setWalletTo(e.target.value)} />
                        <button className="tg-btn primary sm" onClick={() => loadWalletTopups(walletFrom, walletTo)}>Show</button>
                        <button className="tg-btn sm" onClick={() => {
                            const now = new Date();
                            const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                            const to = now.toISOString().slice(0, 10);
                            setWalletFrom(from); setWalletTo(to); loadWalletTopups(from, to);
                        }}>This month</button>
                        <button className="tg-btn sm" onClick={() => {
                            const now = new Date();
                            const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                            const from = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-01`;
                            const last = new Date(now.getFullYear(), now.getMonth(), 0);
                            const to = `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
                            setWalletFrom(from); setWalletTo(to); loadWalletTopups(from, to);
                        }}>Last month</button>
                        <button className="tg-btn sm" onClick={() => { setWalletFrom(''); setWalletTo(''); loadWalletTopups('', ''); }}>All time</button>
                        <span style={{ fontSize: 13, opacity: 0.85, marginLeft: 6 }}>
                            <b>${(walletTotalCents / 100).toFixed(2)}</b> in <b>{walletTopups.length}</b> top-up(s)
                        </span>
                    </div>

                    {walletAccounts.length > 0 && (
                        <table className="tg-table" style={{ marginBottom: 18 }}>
                            <thead>
                                <tr>
                                    <th>Telegram account</th>
                                    <th style={{ width: 110 }}>Top-ups</th>
                                    <th style={{ width: 130 }}>Total in</th>
                                    <th style={{ width: 160 }}>Last top-up</th>
                                </tr>
                            </thead>
                            <tbody>
                                {walletAccounts.map(a => (
                                    <tr key={a.telegramUserId}>
                                        <td>
                                            <b>{a.username ? '@' + a.username : a.firstName || a.telegramUserId}</b>
                                            {a.note && <span style={{ opacity: 0.85 }}> ({a.note})</span>}
                                            <div style={{ fontSize: 11, opacity: 0.6 }}>{a.telegramUserId}</div>
                                        </td>
                                        <td>{a.topups}</td>
                                        <td><b>${(a.cents / 100).toFixed(2)}</b></td>
                                        <td>{String(a.lastAt || '').slice(0, 16).replace('T', ' ')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {!walletTopups.length ? (
                        <p className="tg-sub">No top-ups in this period.</p>
                    ) : (
                        <table className="tg-table" style={{ marginBottom: 26 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 160 }}>When</th>
                                    <th>Account</th>
                                    <th style={{ width: 110 }}>Amount</th>
                                    <th style={{ width: 110 }}>Paid with</th>
                                    <th style={{ width: 130 }}>Balance after</th>
                                </tr>
                            </thead>
                            <tbody>
                                {walletTopups.map(t => (
                                    <tr key={t.id}>
                                        <td>{String(t.createdAt || '').slice(0, 16).replace('T', ' ')}</td>
                                        <td>
                                            <b>{t.username ? '@' + t.username : t.telegramUserId}</b>
                                            {t.note && <span style={{ opacity: 0.85 }}> ({t.note})</span>}
                                            <div style={{ fontSize: 11, opacity: 0.6 }}>{t.telegramUserId}</div>
                                        </td>
                                        <td><b>${(t.cents / 100).toFixed(2)}</b></td>
                                        <td>{t.source}</td>
                                        <td>${(t.balanceAfter / 100).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <h3 style={{ marginTop: 0 }}>Payments to check</h3>
                    <p className="tg-sub">
                        Binance payments verify themselves against your transaction history. Alipay, WeChat Pay and
                        any other method you add have no such API, so they land here: look at the payment in your
                        own app, then credit what you actually received &mdash; not what the buyer typed.
                    </p>

                    {!deposits.length ? (
                        <p className="tg-sub">Nothing waiting.</p>
                    ) : (
                        <table className="tg-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 60 }}>#</th>
                                    <th style={{ width: 130 }}>Method</th>
                                    <th>Buyer &amp; reference</th>
                                    <th style={{ width: 90 }}>Proof</th>
                                    <th style={{ width: 110 }}>They say</th>
                                    <th style={{ width: 230 }}>Credit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deposits.map(d => (
                                    <tr key={d.id}>
                                        <td>{d.id}</td>
                                        <td><b>{d.methodLabel}</b></td>
                                        <td>
                                            <div>{d.telegramUserId}</div>
                                            {d.reference && (
                                                <div style={{ fontSize: 12, opacity: 0.75, wordBreak: 'break-word' }}>{d.reference}</div>
                                            )}
                                            <div style={{ fontSize: 11, opacity: 0.55 }}>{String(d.createdAt || '').slice(0, 16).replace('T', ' ')}</div>
                                        </td>
                                        <td>
                                            {d.proofPath
                                                ? <a href={`${API_URL}${d.proofPath}`} target="_blank" rel="noreferrer">
                                                    <img src={`${API_URL}${d.proofPath}`} alt="receipt"
                                                        style={{ width: 64, height: 44, objectFit: 'cover', borderRadius: 6 }} />
                                                  </a>
                                                : <span style={{ opacity: 0.5, fontSize: 12 }}>none</span>}
                                        </td>
                                        <td>{d.claimedCents ? `$${(d.claimedCents / 100).toFixed(2)}` : <span style={{ opacity: 0.5 }}>—</span>}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                <span>$</span>
                                                <input className="tg-input" style={{ width: 80 }} type="number" step="0.01" min="0"
                                                    value={amountEdits[d.id] ?? (d.claimedCents ? (d.claimedCents / 100).toFixed(2) : '')}
                                                    onChange={e => setAmountEdits({ ...amountEdits, [d.id]: e.target.value })} />
                                                <button className="tg-btn primary sm" disabled={busy} onClick={() => approveDeposit(d)}>Approve</button>
                                                <button className="tg-btn sm" disabled={busy} onClick={() => rejectDeposit(d)}>Reject</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <h3 style={{ marginTop: 26 }}>Payment methods</h3>
                    <p className="tg-sub">
                        Each one shows the buyer your QR and your instructions, then asks for the amount and a
                        transaction number or screenshot. Untick Active to hide a method without deleting it.
                    </p>

                    <table className="tg-table">
                        <thead>
                            <tr>
                                <th style={{ width: 70 }}>Icon</th>
                                <th style={{ width: 150 }}>Name</th>
                                <th>Instructions shown to the buyer</th>
                                <th style={{ width: 150 }}>QR code</th>
                                <th style={{ width: 80 }}>Active</th>
                                <th style={{ width: 80 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {methods.map(m => (
                                <tr key={m.key}>
                                    <td>
                                        <input className="tg-input" style={{ width: 56, textAlign: 'center', fontSize: 18 }}
                                            defaultValue={m.emoji || ''}
                                            onBlur={e => saveMethod(m.key, { emoji: e.target.value })} />
                                    </td>
                                    <td>
                                        <input className="tg-input" defaultValue={m.label}
                                            onBlur={e => saveMethod(m.key, { label: e.target.value })} />
                                    </td>
                                    <td>
                                        <textarea className="tg-input" rows={3} defaultValue={m.instructions || ''}
                                            placeholder="Scan the QR, pay the amount you want to add, then send the transaction number here."
                                            onBlur={e => saveMethod(m.key, { instructions: e.target.value })} />
                                    </td>
                                    <td>
                                        {m.qrPath ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <img src={`${API_URL}${m.qrPath}`} alt="QR"
                                                    style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }} />
                                                <button className="tg-btn sm" disabled={busy}
                                                    onClick={() => saveMethod(m.key, { qrPath: '' })}>Remove</button>
                                            </div>
                                        ) : (
                                            <input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy}
                                                style={{ fontSize: 12, maxWidth: 140 }}
                                                onChange={e => uploadMethodQr(m.key, e.target.files?.[0])} />
                                        )}
                                    </td>
                                    <td>
                                        <input type="checkbox" checked={!!m.active}
                                            onChange={e => saveMethod(m.key, { active: e.target.checked ? 1 : 0 })} />
                                    </td>
                                    <td>
                                        <button className="tg-btn sm" disabled={busy}
                                            onClick={async () => {
                                                if (!window.confirm(`Delete "${m.label}"? Payments already credited are kept.`)) return;
                                                await authFetch(`/admin/telegram/payment-methods/${m.key}`, { method: 'DELETE' });
                                                loadMethods();
                                            }}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
                        <input className="tg-input" style={{ width: 140 }} placeholder="id, e.g. alipay"
                            value={newMethod.key} onChange={e => setNewMethod({ ...newMethod, key: e.target.value })} />
                        <input className="tg-input" style={{ width: 200 }} placeholder="Name, e.g. Alipay"
                            value={newMethod.label} onChange={e => setNewMethod({ ...newMethod, label: e.target.value })} />
                        <button className="tg-btn primary sm" disabled={busy} onClick={addMethod}>Add method</button>
                    </div>
                </div>
            )}

            {sub === 'categories' && <TelegramCategoryManager onChanged={loadCategories} />}

            {sub === 'categories' && (
                <div className="tg-card">
                    <h3 style={{ marginTop: 0 }}>Shop categories</h3>
                    <p className="tg-sub">
                        The bot groups products into categories automatically, from the product name —
                        add an Adobe product and it appears under Adobe. Create your own categories and move
                        products in the section above; here you change what buyers <b>see</b>: each category's icon.
                    </p>

                    <div className="tg-note" style={{ marginBottom: 16 }}>
                        <b>What Telegram lets a shop control — and what it does not</b>
                        <ul style={{ margin: '8px 0 12px 18px', padding: 0, lineHeight: 1.65 }}>
                            <li>
                                <b>Button colour: four styles, set below.</b> Telegram accepts exactly{' '}
                                <code>default</code>, <code>primary</code>, <code>success</code> and{' '}
                                <code>danger</code> — checked against the live API, which refuses the whole
                                message for anything else. They are names, not colours: each Telegram app paints
                                them in its own palette (primary is the accent blue/teal, success green, danger
                                red), so a shop looks consistent without matching one exact shade. There is no
                                hex or free colour picker in the Bot API.
                            </li>
                            <li>
                                <b>A picture cannot go inside a button.</b> A button is text, one emoji and a
                                colour. An uploaded image is shown as a <b>banner at the top of the category
                                screen</b>. For a logo <i>on</i> the button, use the Custom emoji ID below.
                            </li>
                        </ul>
                        <b>Icons &mdash; two ways, both available now</b>
                        <ol style={{ margin: '8px 0 0 18px', padding: 0, lineHeight: 1.65 }}>
                            <li>
                                <b>An emoji.</b> Paste any emoji into the Emoji box. Nothing else needed.
                            </li>
                            <li>
                                <b>A real logo, from your own image.</b> Use <b>Icon from image</b> below. The bot
                                resizes it to Telegram&rsquo;s 100&times;100 and turns it into a custom emoji that
                                the bot itself owns, then puts that on the button &mdash; the same thing the
                                &ldquo;send an image, get an emoji ID&rdquo; bots do, except we are that bot. No
                                Fragment purchase and no third party is involved.
                            </li>
                            <li>
                                One-time setup: send <code>/id</code> to the bot, then paste the number into{' '}
                                <b>Icon pack owner</b> below. Telegram requires a user to own a sticker pack, and
                                that is the only thing it needs you for.
                            </li>
                            <li>
                                The plain emoji stays as a fallback, so a client that cannot draw the custom one
                                still shows an icon. Clear both boxes to go back to our built-in icon.
                            </li>
                        </ol>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 14px' }}>
                        <b style={{ fontSize: 13 }}>Button colour for the whole shop</b>
                        <select className="tg-input" style={{ width: 160 }} value={defaultStyle}
                            onChange={e => setDefaultStyle(e.target.value)}>
                            {buttonStyles.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <span style={{ fontSize: 12, opacity: 0.7 }}>
                            Applies to every category and product button. A category can override it below.
                        </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 14px' }}>
                        <b style={{ fontSize: 13 }}>US dollars per Adobe credit</b>
                        <input className="tg-input" style={{ width: 100 }} type="number" step="0.01" min="0.01"
                            value={settings.usdPerCredit ?? '1'}
                            onChange={e => setSettings({ ...settings, usdPerCredit: e.target.value })} />
                        <span style={{ fontSize: 12, opacity: 0.7 }}>
                            What an agent pays, from their bot balance, for one credit in the Top up Adobe credits menu.
                        </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 14px' }}>
                        <b style={{ fontSize: 13 }}>Icon pack owner</b>
                        <input className="tg-input" style={{ width: 180 }} placeholder="your Telegram ID"
                            value={settings.iconPackOwnerId || ''}
                            onChange={e => setSettings({ ...settings, iconPackOwnerId: e.target.value })} />
                        <span style={{ fontSize: 12, opacity: 0.7 }}>
                            Send <code>/id</code> to the bot to get this. Needed once, before uploading icons.
                        </span>
                    </div>

                    {!categories.length ? (
                        <p className="tg-sub">No categories yet — price a few products and they will appear here.</p>
                    ) : (
                        <>
                            <table className="tg-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 90 }}>Emoji</th>
                                        <th>Category</th>
                                        <th style={{ width: 110 }}>Products</th>
                                        <th style={{ width: 175 }}>Custom emoji ID</th>
                                        <th style={{ width: 165 }}>Icon from image</th>
                                        <th style={{ width: 120 }}>Colour</th>
                                        <th style={{ width: 170 }}>Banner image</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.map(c => (
                                        <tr key={c.slug}>
                                            <td>
                                                <input className="tg-input" style={{ width: 70, fontSize: 18, textAlign: 'center' }}
                                                    value={iconEdits[c.slug]?.emoji ?? c.emoji}
                                                    onChange={e => setIconEdits({ ...iconEdits, [c.slug]: { ...(iconEdits[c.slug] || { customEmojiId: c.customEmojiId }), emoji: e.target.value } })} />
                                            </td>
                                            <td><b>{c.label}</b></td>
                                            <td>{c.products} · {c.inStock} in stock</td>
                                            <td>
                                                <input className="tg-input" placeholder="optional — long number"
                                                    value={iconEdits[c.slug]?.customEmojiId ?? c.customEmojiId}
                                                    onChange={e => setIconEdits({ ...iconEdits, [c.slug]: { ...(iconEdits[c.slug] || { emoji: c.emoji }), customEmojiId: e.target.value } })} />
                                            </td>
                                            <td>
                                                <input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy}
                                                    style={{ fontSize: 12, maxWidth: 155 }}
                                                    title="Turns your logo into a custom emoji on the button"
                                                    onChange={e => uploadCategoryIcon(c.slug, e.target.files?.[0])} />
                                            </td>
                                            <td>
                                                <select className="tg-input"
                                                    value={iconEdits[c.slug]?.style ?? c.style ?? ''}
                                                    onChange={e => setIconEdits({ ...iconEdits, [c.slug]: { ...(iconEdits[c.slug] || { emoji: c.emoji, customEmojiId: c.customEmojiId }), style: e.target.value } })}>
                                                    <option value="">shop default</option>
                                                    {buttonStyles.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </td>
                                            <td>
                                                {c.imageUrl ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <img src={`${API_URL}${c.imageUrl}`} alt="" style={{ width: 54, height: 34, objectFit: 'cover', borderRadius: 6 }} />
                                                        <button className="tg-btn sm" disabled={busy} onClick={() => removeCategoryImage(c.slug)}>Remove</button>
                                                    </div>
                                                ) : (
                                                    <input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy}
                                                        style={{ fontSize: 12, maxWidth: 170 }}
                                                        onChange={e => uploadCategoryImage(c.slug, e.target.files?.[0])} />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div style={{ marginTop: 14 }}>
                                <button className="tg-btn primary sm" disabled={busy} onClick={saveCategories}>Save icons</button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {sub === 'suppliers' && <TelegramSuppliersAdmin />}
            {sub === 'notices' && <TelegramNoticesAdmin />}
            {sub === 'settings' && <TelegramMessageIcons />}

            {sub === 'settings' && (
                <div className="tg-card">
                    <h3>Settings</h3>
                    <p className="tg-sub">
                        The bot token and Binance keys live in <code>server/.env</code> on the server, never here —
                        this page only shows whether they are present.
                    </p>
                    <div className="tg-flags" style={{ marginBottom: 14 }}>
                        <label>
                            <input type="checkbox" checked={settings.paymentsEnabled !== '0'}
                                onChange={e => setSettings({ ...settings, paymentsEnabled: e.target.checked ? '1' : '0' })} />
                            {' '}Accept online payments
                            <span style={{ display: 'block', fontWeight: 400, fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                                Untick to stop taking new deposits. Buyers are told deposits are paused, and any
                                balance they already hold still works &mdash; money already paid in stays theirs.
                                This is the switch for turning payments off; the webhook URL below is not.
                            </span>
                        </label>
                    </div>

                    <div className="tg-flags" style={{ marginBottom: 14 }}>
                        <label>
                            <input type="checkbox" checked={settings.priceFollowShop === '1'}
                                onChange={e => setSettings({ ...settings, priceFollowShop: e.target.checked ? '1' : '0' })} />
                            {' '}Follow Shop prices automatically
                            <span style={{ display: 'block', fontWeight: 400, fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                                Any option with no price typed on the Catalogue page is priced from the Shop’s own
                                price, so changing a price in the Shop changes it in the bot straight away. A price
                                typed here always wins — clear that box to hand it back to the Shop.
                            </span>
                        </label>
                        {settings.priceFollowShop === '1' && (
                            <label style={{ display: 'block', marginTop: 8, fontWeight: 400 }}>
                                US dollars per 1 Shop price unit{' '}
                                <input type="number" step="0.01" min="0.01" style={{ width: 90 }}
                                    value={settings.usdPerShopUnit ?? '1'}
                                    onChange={e => setSettings({ ...settings, usdPerShopUnit: e.target.value })} />
                                <span style={{ display: 'block', fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                                    1 means a Shop price of 45 is sold at $45.00 in the bot.
                                </span>
                            </label>
                        )}
                    </div>

                    <div className="tg-flags" style={{ marginBottom: 14 }}>
                        <label>
                            <input type="checkbox" checked={settings.restockBroadcast !== '0'}
                                onChange={e => setSettings({ ...settings, restockBroadcast: e.target.checked ? '1' : '0' })} />
                            {' '}Announce restocks to buyers
                            <span style={{ display: 'block', fontWeight: 400, fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                                When you import codes for a product the bot sells, every buyer gets
                                “Added / Current stock / Unit price” with a Buy button.
                            </span>
                        </label>
                        <label>
                            <input type="checkbox" checked={settings.supplierAlerts !== '0'} disabled={settings.restockBroadcast === '0'}
                                onChange={e => setSettings({ ...settings, supplierAlerts: e.target.checked ? '1' : '0' })} />
                            {' '}Also announce supplier restocks &amp; new supplier products
                            <span style={{ display: 'block', fontWeight: 400, fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                                When a linked supplier adds a product the bot sells, or a sold-out one comes back (or its stock jumps),
                                buyers are told — also for your own products restocked from that supplier. At most 5 per
                                15-minute sync, and each product at most once every 12 hours, so buyers are not spammed.
                            </span>
                        </label>
                        <label>
                            <input type="checkbox" checked={settings.announceToChannel !== '0'} disabled={settings.restockBroadcast === '0'}
                                onChange={e => setSettings({ ...settings, announceToChannel: e.target.checked ? '1' : '0' })} />
                            {' '}Also post these announcements in the channel
                            <span style={{ display: 'block', fontWeight: 400, fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                                New products, restocks and promotions are posted in your channel (the Channel link above) too, with a
                                Buy button that opens the bot on that product. The bot must be an admin of the channel with “Post messages”.
                            </span>
                        </label>
                    </div>
                    <label>Welcome message
                        <textarea rows={3} value={settings.welcomeText} onChange={e => setSettings({ ...settings, welcomeText: e.target.value })} />
                    </label>
                    <div className="tg-grid">
                        <label>Channel link
                            <input value={settings.channelLink}
                                onChange={e => setSettings({ ...settings, channelLink: e.target.value })}
                                placeholder="@YourChannel — shown in the welcome message" />
                        </label>
                        <label>Support contact
                            <input value={settings.supportContact}
                                onChange={e => setSettings({ ...settings, supportContact: e.target.value })}
                                placeholder="@YourSupport" />
                        </label>
                    </div>
                    <div className="tg-flags" style={{ marginBottom: 14 }}>
                        <label>
                            <input type="checkbox" checked={settings.forceJoin !== '0'}
                                onChange={e => setSettings({ ...settings, forceJoin: e.target.checked ? '1' : '0' })} />
                            {' '}Buyers must join the channel before using the bot
                            <span style={{ display: 'block', fontWeight: 400, fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                                Anyone not in the channel above gets a “Join Channel” screen instead of the shop.
                            </span>
                            {settings.forceJoin !== '0' && status.joinGate && (
                                <span style={{ display: 'block', fontWeight: 500, fontSize: 12, marginTop: 4,
                                    color: status.joinGate.canCheck ? '#16a34a' : '#d97706' }}>
                                    {status.joinGate.canCheck
                                        ? `✓ Active — checking membership of @${status.joinGate.channel}`
                                        : `⚠ Not enforced yet: ${status.joinGate.detail}`}
                                </span>
                            )}
                        </label>
                    </div>
                    <label style={{ display: 'none' }}>Support message
                        <textarea rows={2} value={settings.supportContact}
                            onChange={e => setSettings({ ...settings, supportContact: e.target.value })}
                            placeholder="Shown when a buyer taps Support — e.g. Message @yourname, we reply within an hour." />
                    </label>
                    <div className="tg-grid">
                        <label>Admin Telegram ids (comma separated)
                            <input value={settings.adminChatIds} onChange={e => setSettings({ ...settings, adminChatIds: e.target.value })} placeholder="Send /id to the bot to find yours" />
                        </label>
                        <label>Currency
                            <input value={settings.currency} onChange={e => setSettings({ ...settings, currency: e.target.value })} placeholder="USDT" />
                        </label>
                    </div>
                    <div className="tg-grid">
                        <label>Binance webhook URL (not used)
                            <input value={settings.webhookUrl} onChange={e => setSettings({ ...settings, webhookUrl: e.target.value })}
                                placeholder="https://reseller.ado-besoft.com/api/telegram-shop/binance-webhook" />
                        </label>
                        <label>Unpaid order expiry (minutes)
                            <input type="number" min="5" value={settings.orderExpiryMinutes} onChange={e => setSettings({ ...settings, orderExpiryMinutes: e.target.value })} />
                        </label>
                    </div>
                        <span style={{ display: 'block', fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                            Left over from the merchant API, which this shop does not use. Payments are proved by
                            the Order ID a buyer pastes, checked against your own Binance transaction history, so
                            editing or clearing this field does not affect them either way.
                        </span>
                    <div className="tg-actions">
                        <div style={{ marginBottom: 14 }}>
                        <label className="tg-label">Site address (for images the bot sends)</label>
                        <input className="tg-input" placeholder="https://reseller.ado-besoft.com"
                            value={settings.publicBaseUrl || ''}
                            onChange={e => setSettings({ ...settings, publicBaseUrl: e.target.value })} />
                        <span style={{ display: 'block', fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                            Telegram fetches category banners from here, so it has to be reachable from the
                            internet. Leave it blank only if the webhook URL above is still correct.
                        </span>
                    </div>

                    <button className="tg-btn primary" onClick={saveSettings}>Save settings</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TelegramShopAdmin;
